// Task 7 — create/edit dialog for /admin/teams. Modal structure mirrors
// queue-instructions/components/InstructionDialog.tsx. Fields: name
// (1-100, counter), lead (picker fed by the tenant users list — hoisted to
// TeamsAdminPage per /simplify fix G so the page, this dialog, and
// MembersPanel all share one GET /users fetch instead of three), parent
// team (top-level + unarchived teams, excluding self + descendants in edit
// mode — server re-validates via TEAM_CYCLE, this is UX prevention only).
import { useEffect, useMemo, useState } from "react";
import { ApiError } from "@/lib/api-client";
import { toast } from "@/components/Toaster";
import { getApiErrorCode, showApiError } from "@/lib/error-toast";
import { teamsApi, type TeamRecord, type TenantUser } from "../api/client";
import { buildChildrenIndex, collectDescendantIds } from "../lib/tree";
import styles from "../teams.module.css";

const NAME_MAX = 100;

export interface TeamDialogProps {
  /** Team being edited, or null when creating. */
  team: TeamRecord | null;
  /** Full team list — used to compute parent-select options. */
  teams: TeamRecord[];
  /** Tenant users — lead-picker source, fetched once by the page (fix G). */
  users: TenantUser[];
  onClose: () => void;
  onSaved: () => void;
}

export function TeamDialog({ team, teams, users, onClose, onSaved }: TeamDialogProps) {
  const isEdit = team !== null;
  const [name, setName] = useState(team?.name ?? "");
  const [supervisorUserId, setSupervisorUserId] = useState(team?.supervisorUserId ?? "");
  const [parentTeamId, setParentTeamId] = useState(team?.parentTeamId ?? "");
  const [nameError, setNameError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const parentOptions = useMemo(() => {
    const excluded = new Set<string>(
      isEdit ? [team!.id, ...collectDescendantIds(team!.id, buildChildrenIndex(teams))] : [],
    );
    return teams.filter((t) => !t.archivedAt && !excluded.has(t.id));
  }, [teams, isEdit, team]);

  const trimmedName = name.trim();
  const canSave =
    trimmedName.length > 0 && trimmedName.length <= NAME_MAX && supervisorUserId !== "" && !busy;

  // Task 11/T2 fix — window-level listener (FieldCreateModal idiom), not a
  // div-level onKeyDown. The div-level handler goes deaf once focus lands
  // on <body> (e.g. after clicking a non-focusable spot in the dialog),
  // because keydown never reaches the div. Window always sees it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function handleNameChange(value: string) {
    setName(value);
    if (nameError) setNameError(null);
  }

  async function handleSave() {
    setBusy(true);
    setNameError(null);
    try {
      if (isEdit) {
        await teamsApi.update(team!.id, {
          name: trimmedName,
          supervisorUserId,
          parentTeamId: parentTeamId || null,
        });
      } else {
        await teamsApi.create({
          name: trimmedName,
          supervisorUserId,
          ...(parentTeamId ? { parentTeamId } : {}),
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof ApiError) {
        const code = getApiErrorCode(e);
        if (e.status === 409 && code === "team_name_conflict") {
          setNameError("A team with this name exists.");
          return;
        }
        if (e.status === 400 && code === "TEAM_CYCLE") {
          toast.error("That change would create a team cycle — choose a different parent.");
          return;
        }
      }
      showApiError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <h2>{isEdit ? "Edit team" : "New team"}</h2>

        <div className={styles.field}>
          <label htmlFor="team-name">
            <span>Name</span>
          </label>
          <input
            id="team-name"
            type="text"
            value={name}
            maxLength={NAME_MAX}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., West Region Ops"
            aria-label="name"
            autoFocus
          />
          <small className={styles.help}>
            {name.length} / {NAME_MAX}
          </small>
          {nameError && <span className={styles.fieldError}>{nameError}</span>}
        </div>

        <div className={styles.field}>
          <label htmlFor="team-lead">
            <span>Lead</span>
          </label>
          <select
            id="team-lead"
            value={supervisorUserId}
            aria-label="lead"
            onChange={(e) => setSupervisorUserId(e.target.value)}
          >
            <option value="">— select a lead —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="team-parent">
            <span>Parent team</span>
          </label>
          <select
            id="team-parent"
            value={parentTeamId}
            aria-label="parent team"
            onChange={(e) => setParentTeamId(e.target.value)}
          >
            <option value="">— top level —</option>
            {parentOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.actions}>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.primary} disabled={!canSave} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
